import { useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Pencil, Trash2, Plus, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/Forms";
import { Tag } from "@/components/kit";
import { ConfirmDialog, DIALOG_CLS } from "@/components/Dialogs";
import { useStore } from "@/store/AppStore";
import { teamOf, nameMap } from "@/lib/selectors";
import { STATUS, assigneeLabel } from "@/lib/tasks";

const PSTATUS = { Ativo: "green", "Em revisão": "gold", Rascunho: "muted" };

function ProcessForm({ initial, people, template, onSave }) {
  const [f, setF] = useState({ name: "", area: "", role: "", status: "Ativo", responsibleId: "owner", ...initial, steps: (initial?.steps || []).join("\n"), ...(initial && { responsibleId: initial.responsibleId || "owner" }) });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const submit = (e) => {
    e.preventDefault();
    const steps = f.steps.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!f.name.trim() || !f.area.trim()) return toast.error("Informe nome e setor.");
    if (!steps.length) return toast.error("Inclua pelo menos uma etapa.");
    onSave({ ...f, name: f.name.trim(), area: f.area.trim(), steps, responsibleId: f.responsibleId === "owner" ? null : f.responsibleId });
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="process-form">
      <Field label="Nome do processo"><Input value={f.name} onChange={set("name")} data-testid="process-name-input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Setor"><Input value={f.area} onChange={set("area")} placeholder="Ex.: Caixa" data-testid="process-area-input" /></Field>
        <Field label="Função"><Input value={f.role} onChange={set("role")} placeholder="Ex.: Vendedora" data-testid="process-role-input" /></Field>
      </div>
      {!template && (
        <div className="grid grid-cols-2 gap-4">
          <Field label="Status">
            <Select value={f.status} onValueChange={set("status")}><SelectTrigger data-testid="process-status-select"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(PSTATUS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Responsável">
            <Select value={f.responsibleId} onValueChange={set("responsibleId")}><SelectTrigger data-testid="process-responsible-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="owner">Empresária</SelectItem>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
          </Field>
        </div>
      )}
      <Field label="Etapas (uma por linha)"><Textarea rows={5} value={f.steps} onChange={set("steps")} data-testid="process-steps-input" /></Field>
      <Button type="submit" className="w-full font-semibold" data-testid="process-save-button">Salvar</Button>
    </form>
  );
}

export function ProcessDialog({ open, onOpenChange, initial, people = [], template, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={DIALOG_CLS} data-testid="process-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{initial?.id ? "Editar" : "Novo"} {template ? "modelo" : "processo"}</DialogTitle></DialogHeader>
        {open && <ProcessForm initial={initial} people={people} template={template} onSave={onSave} />}
      </DialogContent>
    </Dialog>
  );
}

function ProcessRow({ p, tasks, names, onEdit, onDelete, onMove }) {
  const [openSteps, setOpenSteps] = useState(false);
  return (
    <li data-testid={`process-row-${p.id}`} className="py-4">
      <div className="flex items-start gap-2">
        <button onClick={() => setOpenSteps(!openSteps)} className="min-w-0 flex-1 text-left" data-testid={`process-toggle-${p.id}`}>
          <p className="flex items-center gap-2 text-sm font-semibold"><Layers className="h-4 w-4 shrink-0 text-gold" /> {p.name}</p>
          <div className="mt-2 flex flex-wrap gap-1.5 pl-6">
            <Tag>{p.area}</Tag>{p.role && <Tag>{p.role}</Tag>}<Tag tone={PSTATUS[p.status]}>{p.status}</Tag><Tag tone="gold">{tasks.length} tarefa(s)</Tag>
          </div>
          <p className="mt-1.5 pl-6 text-xs text-neutral-500">Responsável: {p.owner}</p>
        </button>
        <div className="flex shrink-0 flex-wrap justify-end">
          <Button variant="ghost" size="icon" aria-label="Subir" data-testid={`process-up-${p.id}`} onClick={() => onMove(-1)}><ChevronUp className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" aria-label="Descer" data-testid={`process-down-${p.id}`} onClick={() => onMove(1)}><ChevronDown className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" aria-label="Editar" data-testid={`process-edit-${p.id}`} onClick={onEdit}><Pencil className="h-4 w-4 text-gold-dark" /></Button>
          <Button variant="ghost" size="icon" aria-label="Excluir" data-testid={`process-delete-${p.id}`} onClick={onDelete}><Trash2 className="h-4 w-4 text-neutral-500" /></Button>
        </div>
      </div>
      {openSteps && (
        <div className="mt-4 grid gap-6 pl-6 md:grid-cols-2" data-testid={`process-details-${p.id}`}>
          <ol className="space-y-2 border-l border-gold/40 pl-5 text-sm text-neutral-700">{p.steps.map((s, i) => <li key={s}><span className="mr-2 font-semibold text-gold-dark">{i + 1}.</span>{s}</li>)}</ol>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-neutral-400">Tarefas vinculadas</p>
            {!tasks.length && <p className="text-xs text-neutral-400">Vincule tarefas a este processo ao criar ou editar uma tarefa.</p>}
            <ul className="space-y-1.5">{tasks.map((t) => <li key={t.id} className="text-xs text-neutral-700">{t.title} <span className="text-neutral-400">· {assigneeLabel(t, names)} · {STATUS[t.status].label}</span></li>)}</ul>
          </div>
        </div>
      )}
    </li>
  );
}

export function ProcessManager({ companyId }) {
  const { state, saveProcess, deleteProcess, moveProcess } = useStore();
  const [area, setArea] = useState("Todos");
  const [dlg, setDlg] = useState(null);
  const company = state.companies.find((c) => c.id === companyId);
  const people = teamOf(state, companyId);
  const names = nameMap(state);
  const all = state.processes.filter((p) => p.companyId === companyId);
  const areas = ["Todos", ...new Set(all.map((p) => p.area))];
  const list = all.filter((p) => area === "Todos" || p.area === area);
  const save = (p) => {
    saveProcess({ ...p, companyId, owner: people.find((x) => x.id === p.responsibleId)?.name || company.owner });
    toast.success(p.id ? "Processo atualizado." : "Processo criado.");
    setDlg(null);
  };
  return (
    <section data-testid="process-manager" className="rounded-xl border border-neutral-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] sm:p-7">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold">{all.length} processos mapeados</h2>
        <div className="flex flex-wrap gap-2">
          <Button className="h-9 font-semibold" onClick={() => setDlg({ type: "edit" })} data-testid="new-process-button"><Plus className="mr-1 h-4 w-4" /> Novo processo</Button>
        </div>
      </div>
      <div className="mb-2 flex gap-1.5 overflow-x-auto pb-2">
        {areas.map((a) => <button key={a} onClick={() => setArea(a)} data-testid={`process-area-${a}`} className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium ${area === a ? "border-gold bg-gold text-neutral-900" : "border-neutral-200 text-neutral-600 hover:border-gold"}`}>{a}</button>)}
      </div>
      <ul className="divide-y divide-neutral-100">
        {list.map((p) => (
          <ProcessRow key={p.id} p={p} names={names} tasks={state.tasks.filter((t) => t.processId === p.id)} onEdit={() => setDlg({ type: "edit", p })} onDelete={() => setDlg({ type: "del", p })} onMove={(d) => moveProcess(p.id, d)} />
        ))}
      </ul>
      <ProcessDialog open={dlg?.type === "edit"} onOpenChange={(v) => !v && setDlg(null)} initial={dlg?.p} people={people} onSave={(x) => save({ ...x, id: dlg?.p?.id })} />
      <ConfirmDialog open={dlg?.type === "del"} onOpenChange={(v) => !v && setDlg(null)} title="Excluir processo?" description={`${dlg?.p?.name || ""}. As tarefas vinculadas continuam, sem o vínculo.`} testId="process-delete-dialog" onConfirm={() => { deleteProcess(dlg.p.id); toast.success("Processo excluído."); }} />
    </section>
  );
}
